import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api, ApiError, uploadObject } from '../api/client';
import type { UploadHandle } from '../api/client';
import { useAsync } from '../lib/hooks';
import { useLang } from '../lib/useLang';
import { formatBytes, formatEUR } from '../lib/format';
import { writeCostForFiles } from '../lib/quote';
import { PageHeader } from '../components/PageHeader';
import { ApiState, EmptyState } from '../components/States';
import { Button, Card, CardHead } from '../components/ui';
import { Field } from '../components/Field';
import { useToast } from '../components/Toast';
import { IconUpload, IconPlus, IconClose, IconCheck } from '../components/Icons';

type ItemStatus = 'queued' | 'uploading' | 'done' | 'error';
interface Item {
  key: string;
  file: File;
  status: ItemStatus;
  progress: number;
  error?: string;
}

const MAX_CONCURRENT = 3;

export function Upload() {
  const { t } = useTranslation();
  const archives = useAsync(() => api.listArchives(200), []);

  return (
    <>
      <PageHeader title={t('upload.title')} subtitle={t('upload.subtitle')} />
      <ApiState {...archives} onRetry={archives.reload}>
        {(page) =>
          page.items.length === 0 ? (
            <Card>
              <EmptyState
                title={t('upload.needArchives')}
                action={
                  <Link className="btn btn--primary" to="/archives">
                    <IconPlus width={18} height={18} />
                    {t('upload.createFirst')}
                  </Link>
                }
              />
            </Card>
          ) : (
            <Uploader archives={page.items.map((a) => ({ id: a.id, name: a.name }))} />
          )
        }
      </ApiState>
    </>
  );
}

function Uploader({ archives }: { archives: { id: string; name: string }[] }) {
  const { t } = useTranslation();
  const lang = useLang();
  const toast = useToast();
  const [archiveId, setArchiveId] = useState(archives[0]?.id ?? '');
  const [items, setItems] = useState<Item[]>([]);
  // Quote the queued files before anything is written — the customer should
  // never trigger a chargeable upload without seeing what it costs.
  const pricing = useAsync(() => api.pricing(), []);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const handles = useRef<Map<string, UploadHandle>>(new Map());

  const update = useCallback((key: string, patch: Partial<Item>) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  }, []);

  const addFiles = (files: FileList | null) => {
    if (!files) return;
    const next: Item[] = Array.from(files).map((file, i) => ({
      key: `${Date.now()}-${i}-${file.name}`,
      file,
      status: 'queued',
      progress: 0,
    }));
    setItems((prev) => [...prev, ...next]);
  };

  const runUpload = useCallback(
    (item: Item) =>
      new Promise<boolean>((resolve) => {
        update(item.key, { status: 'uploading', progress: 0, error: undefined });
        const handle = uploadObject(archiveId, item.file, {
          onProgress: (f) => update(item.key, { progress: f }),
        });
        handles.current.set(item.key, handle);
        handle.promise
          .then(() => {
            update(item.key, { status: 'done', progress: 1 });
            resolve(true);
          })
          .catch((err: unknown) => {
            const msg = err instanceof ApiError ? err.message : String(err);
            update(item.key, { status: 'error', error: msg });
            resolve(false);
          })
          .finally(() => handles.current.delete(item.key));
      }),
    [archiveId, update],
  );

  // Simple concurrency-limited runner over all queued/errored items.
  const startAll = async () => {
    if (!archiveId) {
      toast.error(t('upload.noArchive'));
      return;
    }
    const pending = items.filter((it) => it.status === 'queued' || it.status === 'error');
    let done = 0;
    let i = 0;
    const worker = async () => {
      while (i < pending.length) {
        const item = pending[i++];
        const ok = await runUpload(item);
        if (ok) done += 1;
      }
    };
    await Promise.all(Array.from({ length: Math.min(MAX_CONCURRENT, pending.length) }, worker));
    if (done > 0) toast.success(t('upload.doneToast', { count: done }));
  };

  const retry = (item: Item) => {
    if (!archiveId) return;
    void runUpload(item);
  };

  const remove = (key: string) => {
    handles.current.get(key)?.abort();
    handles.current.delete(key);
    setItems((prev) => prev.filter((it) => it.key !== key));
  };

  const clearDone = () => setItems((prev) => prev.filter((it) => it.status !== 'done'));

  const pendingCount = items.filter((it) => it.status === 'queued' || it.status === 'error').length;

  return (
    <div className="stack">
      <Card>
        <div className="card__body">
          <div className="form-row" style={{ marginBottom: 20 }}>
            <Field label={t('upload.pickArchive')}>
              {(p) => (
                <select className="select" {...p} value={archiveId} onChange={(e) => setArchiveId(e.target.value)}>
                  {archives.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>

          <div
            className={`dropzone${dragging ? ' is-over' : ''}`}
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              addFiles(e.dataTransfer.files);
            }}
          >
            <div className="dropzone__icon">
              <IconUpload width={30} height={30} />
            </div>
            <p className="cell-strong">{t('upload.dropzone')}</p>
            <p className="muted" style={{ fontSize: 13 }}>
              {t('upload.dropzoneHint')}
            </p>
            <input
              ref={inputRef}
              type="file"
              multiple
              className="visually-hidden"
              aria-label={t('upload.choose')}
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </div>
        </div>
      </Card>

      {items.length > 0 ? (
        <Card>
          <CardHead title={t('upload.queued', { count: items.length })}>
            {(() => {
              const pending = items.filter((i) => i.status !== 'done');
              const quote = writeCostForFiles(pricing.data, pending.map((i) => i.file.size));
              const bytes = pending.reduce((n, i) => n + i.file.size, 0);
              if (quote === null) return null;
              return (
                <span className="muted" style={{ fontSize: 13 }}>
                  {t('upload.quote', { size: formatBytes(bytes, lang), cost: formatEUR(quote, lang) })}
                </span>
              );
            })()}
            <Button variant="ghost" size="sm" onClick={clearDone}>
              {t('upload.clearDone')}
            </Button>
            <Button variant="primary" size="sm" onClick={startAll} disabled={pendingCount === 0}>
              <IconUpload width={16} height={16} />
              {t('upload.startAll')}
            </Button>
          </CardHead>
          <div className="card__body">
            {items.map((it) => (
              <div className="upload-item" key={it.key}>
                <div className="upload-item__info">
                  <div className="upload-item__name">{it.file.name}</div>
                  <div className="upload-item__meta">
                    {formatBytes(it.file.size, lang)} · <StatusLabel status={it.status} />
                    {it.error ? <span style={{ color: 'var(--danger)' }}> — {it.error}</span> : null}
                  </div>
                  <div className="progress" role="progressbar" aria-valuenow={Math.round(it.progress * 100)} aria-valuemin={0} aria-valuemax={100}>
                    <div
                      className={`progress__bar${it.status === 'error' ? ' progress__bar--error' : ''}${it.status === 'done' ? ' progress__bar--done' : ''}`}
                      style={{ width: `${Math.round((it.status === 'done' ? 1 : it.progress) * 100)}%` }}
                    />
                  </div>
                </div>
                <div className="row" style={{ gap: 6 }}>
                  {it.status === 'done' ? <IconCheck width={18} height={18} style={{ color: 'var(--success)' }} /> : null}
                  {it.status === 'error' ? (
                    <Button variant="secondary" size="sm" onClick={() => retry(it)}>
                      {t('upload.retry')}
                    </Button>
                  ) : null}
                  <button className="icon-btn" onClick={() => remove(it.key)} aria-label={t('upload.remove')}>
                    <IconClose width={16} height={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
}

function StatusLabel({ status }: { status: ItemStatus }) {
  const { t } = useTranslation();
  const map: Record<ItemStatus, string> = {
    queued: t('upload.statusQueued'),
    uploading: t('upload.statusUploading'),
    done: t('upload.statusDone'),
    error: t('upload.statusError'),
  };
  return <span>{map[status]}</span>;
}
